/**
 * Verification Configuration
 * 
 * BETA MODE: Single user verification (for testing/low traffic)
 * - Creator's initial review auto-verifies the restroom
 * - Useful during beta testing when traffic is low
 * 
 * PRODUCTION MODE: Community verification (anti-spam)
 * - Requires 2nd independent user to review within 15 meters
 * - Prevents fake/spam entries
 * 
 * NOTE: Verification mode is stored in Firestore (server-side) so all users see the same mode.
 */

const VERIFICATION_MODES = {
  PRODUCTION: 'production'
};

const VERIFICATION_CONFIG = {
  // Default mode (fallback if Firestore unavailable)
  defaultMode: VERIFICATION_MODES.PRODUCTION,
  
  // Distance threshold for verification (in kilometers)
  // 15 meters = 0.015 km
  verificationRadiusKm: 0.015,
  
  // Minimum reviews required per mode
  minReviewsRequired: {
    [VERIFICATION_MODES.PRODUCTION]: 2   // Production: needs 2nd independent review
  }
};

// In-memory cache of the current mode (fetched from Firestore)
let cachedMode = null;
let cacheTimestamp = null;
const CACHE_DURATION_MS = 60000; // Cache for 1 minute

/**
 * Get current verification mode from Firestore (with caching)
 * Returns cached value if fresh, otherwise fetches from Firestore
 */
export const getVerificationMode = async () => {
  // Return cached value if still fresh
  if (cachedMode && cacheTimestamp && (Date.now() - cacheTimestamp) < CACHE_DURATION_MS) {
    return cachedMode;
  }
  
  // Fetch from Firestore
  try {
    // Dynamic import to avoid circular dependency
    const firebaseService = (await import('../services/firebaseService.js')).default;
    const mode = await firebaseService.getVerificationMode();
    
    // Update cache
    cachedMode = mode;
    cacheTimestamp = Date.now();
    
    return mode;
  } catch (error) {
    console.warn('Error fetching verification mode from Firestore, using default:', error);
    return VERIFICATION_CONFIG.defaultMode;
  }
};

/**
 * Get verification mode synchronously from cache (for immediate use)
 * Returns cached value or default if not yet fetched
 */
export const getVerificationModeSync = () => {
  return cachedMode || VERIFICATION_CONFIG.defaultMode;
};

/**
 * Set verification mode (admin only) - saves to Firestore
 */
export const setVerificationMode = async (mode) => {
  if (!Object.values(VERIFICATION_MODES).includes(mode)) {
    throw new Error(`Invalid verification mode: ${mode}`);
  }
  
  try {
    // Dynamic import to avoid circular dependency
    const firebaseService = (await import('../services/firebaseService.js')).default;
    await firebaseService.setVerificationMode(mode);
    
    // Update cache immediately
    cachedMode = mode;
    cacheTimestamp = Date.now();
    
    console.log(`✅ Verification mode set to: ${mode.toUpperCase()}`);
    return mode;
  } catch (error) {
    console.error('Error setting verification mode:', error);
    throw error;
  }
};

/**
 * Clear the cache (force refresh on next read)
 */
export const clearVerificationModeCache = () => {
  cachedMode = null;
  cacheTimestamp = null;
};

/**
 * Get verification radius in kilometers
 */
export const getVerificationRadius = () => {
  return VERIFICATION_CONFIG.verificationRadiusKm;
};

/**
 * Get minimum reviews required for current mode
 */
export const getMinReviewsRequired = async () => {
  const mode = await getVerificationMode();
  return VERIFICATION_CONFIG.minReviewsRequired[mode];
};

/**
 * Check if a restroom should be verified based on current mode
 * This is used synchronously in verification logic, using cached mode
 */
export const shouldVerifyRestroom = (restroom, currentUserId) => {
  const mode = getVerificationModeSync();
  const minReviews = VERIFICATION_CONFIG.minReviewsRequired[mode];
  
  // Admin restrooms are always verified
  if (restroom.verified === true) {
    return true;
  }
  
  // Count independent reviews (excluding creator)
  const independentReviews = (restroom.reviews || []).filter(
    review => review.userId !== restroom.createdBy
  );
  
  switch (mode) {
    case VERIFICATION_MODES.PRODUCTION:
      // Production: Need 2nd independent review within radius
      return independentReviews.length >= (minReviews - 1);
      
    default:
      // Default to production logic if mode is unknown
      return independentReviews.length >= (minReviews - 1);
  }
};

/**
 * Check if restroom should be visible to user
 */
export const isRestroomVisible = (restroom, currentUserId, isAdmin) => {
  // Admins see everything
  if (isAdmin) {
    return true;
  }
  
  // Creator always sees their own restroom
  if (restroom.createdBy === currentUserId) {
    return true;
  }
  
  // Everyone else only sees verified restrooms
  return restroom.verified === true;
};

export { VERIFICATION_MODES, VERIFICATION_CONFIG };
export default {
  VERIFICATION_MODES,
  getVerificationMode,
  setVerificationMode,
  getVerificationRadius,
  getMinReviewsRequired,
  shouldVerifyRestroom,
  isRestroomVisible
};
